// src/services/cartService.ts

// Store customer email in localStorage since it's user preference, not cart state
const CUSTOMER_EMAIL_KEY = 'fastspring_customer_email';

// Item with metadata interface
export interface CartItem {
  productId: string;
  quantity: number;
  title?: string;
  price?: string;
  imageUrl?: string;
}

/**
 * Simplified cart service that directly uses FastSpring APIs
 * without local tracking or synchronization
 */
export const cartService = {
  /**
   * Add an item to the cart in FastSpring
   */
  addToCart: (productId: string, metadata?: {title?: string, price?: string, imageUrl?: string}): boolean => {
    try {
      if (!productId || productId === "-") {
        console.error("Invalid product ID");
        return false;
      }

      console.log(`Adding product to cart: ${productId}`);

      // Add to FastSpring if available
      if (window.fastspring && window.fastspring.builder) {
        window.fastspring.builder.add(productId);
        console.log(`Added ${productId} to FastSpring cart`);
        return true;
      } else {
        console.warn("FastSpring not available");
        return false;
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      return false;
    }
  },
  
  /**
   * Remove an item from the cart
   */
  removeFromCart: (productId: string): boolean => {
    try {
      if (!productId || productId === "-") {
        console.error("Invalid product ID for removal");
        return false;
      }

      console.log(`Removing product from cart: ${productId}`);

      // Remove from FastSpring if available
      if (window.fastspring && window.fastspring.builder) {
        window.fastspring.builder.remove(productId);
        console.log(`Removed ${productId} from FastSpring cart`);
        return true;
      } else {
        console.warn("FastSpring not available");
        return false;
      }
    } catch (error) {
      console.error("Error removing from cart:", error);
      return false;
    }
  },
  
  /**
   * Clear the cart
   */
  clearCart: (): boolean => {
    try {
      console.log('Clearing cart');
      
      // Clear FastSpring if available
      if (window.fastspring && window.fastspring.builder) {
        window.fastspring.builder.reset();
        console.log('FastSpring cart reset');
        return true;
      } else {
        console.warn("FastSpring not available");
        return false;
      }
    } catch (error) {
      console.error("Error clearing cart:", error);
      return false;
    }
  },
  
  /**
   * Initialize the cart on page load
   */
  initializeCart: (): void => {
    try {
      console.log('Initializing cart');
      
      if (window.fastspring && window.fastspring.builder) {
        // Try to restore customer email if available
        const email = cartService.getCustomerEmail();
        if (email) {
          window.fastspring.builder.recognize({
            email: email
          });
        }
      } else {
        console.log('FastSpring not available for initialization');
      }
    } catch (error) {
      console.error("Error initializing cart:", error);
    }
  },
  
  /**
   * Save customer email for future use (still useful for checkout)
   */
  saveCustomerEmail: (email: string): void => {
    if (email && email.includes('@')) {
      localStorage.setItem(CUSTOMER_EMAIL_KEY, email);
      
      // Also try to set in FastSpring
      if (window.fastspring && window.fastspring.builder) {
        window.fastspring.builder.recognize({
          email: email
        });
      }
    }
  },
  
  /**
   * Get saved customer email
   */
  getCustomerEmail: (): string | null => {
    return localStorage.getItem(CUSTOMER_EMAIL_KEY);
  },
  
  /**
   * Open FastSpring checkout
   */
  checkout: (): void => {
    try {
      console.log('Opening checkout');
      
      if (window.fastspring && window.fastspring.builder) {
        // If we have a saved email, use it
        const email = cartService.getCustomerEmail();
        if (email) {
          window.fastspring.builder.recognize({
            email: email
          });
        }
        
        window.fastspring.builder.checkout();
      } else {
        console.error("FastSpring not available for checkout");
      }
    } catch (error) {
      console.error("Error opening checkout:", error);
    }
  },

  /**
   * View the cart
   */
  viewCart: (): void => {
    try {
      console.log('Opening cart view');
      
      if (window.fastspring && window.fastspring.builder) {
        // If we have a saved email, use it
        const email = cartService.getCustomerEmail();
        if (email) {
          window.fastspring.builder.recognize({
            email: email
          });
        }
        
        // Use viewCart instead of checkout to go directly to the cart
        window.fastspring.builder.viewCart();
      } else {
        console.error("FastSpring not available for viewing cart");
      }
    } catch (error) {
      console.error("Error opening cart view:", error);
    }
  }
};

// Add types for FastSpring in window object
declare global {
  interface Window {
    fastspring?: {
      builder: {
        add: (productId: string | any[]) => void;
        remove: (productId: string) => void;
        reset: () => void;
        checkout: () => void;
        viewCart: () => void;
        recognize?: (data: { email: string }) => void;
        update?: (options: any) => void;
        cart?: {
          items: Array<{
            product: string;
            quantity?: number;
            display?: string;
          }>;
        };
      };
    };
  }
}